import posthog from "posthog-js";
import { useEffect, useState } from "react";

import type { ConversionFlagKey } from "~/lib/conversion-test-flags";

/** Ad blockers stop PostHog; resolve to the default state rather than wait. */
const FLAG_TIMEOUT_MS = 3000;

let flagsReady = false;
let subscribed = false;
const listeners = new Set<() => void>();

const notify = () => {
	for (const listener of listeners) listener();
};

const ensureSubscription = () => {
	if (subscribed || typeof window === "undefined") return;
	subscribed = true;

	posthog.onFeatureFlags(() => {
		flagsReady = true;
		notify();
	});

	if (posthog.featureFlags?.hasLoadedFlags) {
		flagsReady = true;
		return;
	}

	window.setTimeout(() => {
		if (flagsReady) return;
		flagsReady = true;
		notify();
	}, FLAG_TIMEOUT_MS);
};

/**
 * Reads one conversion-test flag. Reading the flag records exposure, so pass
 * `enabled: false` until the caller would actually use the value.
 *
 * @param {ConversionFlagKey} key - Flag key from `CONVERSION_FLAGS`
 * @param {object} [options]
 * @param {boolean} [options.enabled] - False skips reading the flag
 * @returns {{ value: unknown; ready: boolean }} Raw flag value (undefined when off, loading, or blocked) and whether it is final
 */
const useConversionFlag = (
	key: ConversionFlagKey,
	{ enabled = true }: { enabled?: boolean } = {},
): { value: unknown; ready: boolean } => {
	const [, setVersion] = useState(0);

	useEffect(() => {
		if (!enabled) return;

		const listener = () => setVersion((version) => version + 1);
		listeners.add(listener);
		ensureSubscription();

		return () => {
			listeners.delete(listener);
		};
	}, [enabled]);

	if (!enabled || !flagsReady) {
		return { value: undefined, ready: enabled && flagsReady };
	}

	return { value: posthog.getFeatureFlag(key), ready: true };
};

export default useConversionFlag;
